import {
  computed,
  inject,
  Injectable,
  type Signal,
  signal
} from '@angular/core';
import { Router } from '@angular/router';
import { Usuario } from '@domain/usuario/usuario';
import { AuthHttpRepository } from '@infra/repositories/auth-http.repository';
import { AuthStorageRepository } from '@infra/repositories/auth-storage.repository';
import { DateTime } from 'luxon';
import { debounceTime, firstValueFrom } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private authHttp = inject(AuthHttpRepository);
  private authStorage = inject(AuthStorageRepository);
  private router = inject(Router);

  // Signal que armazena o usuário autenticado
  private readonly _usuario = signal<Usuario | null>(null);
  private _refreshInProgress = signal(false);
  // Computed signal para verificar se o usuário está autenticado
  readonly isAuthenticated = computed(() => this._usuario() !== null);

  constructor() {
    this.carregarUsuarioDoStorage();
  }

  login(login: string, senha: string): void {
    this.authHttp.login(login, senha).subscribe(usuario => {
      this._usuario.set(usuario);
      this.authStorage.saveUsuario(usuario);
    });
  }

  logout() {
    this.authStorage.logout();
    this._usuario.set(null);
    this.router.navigate(['/login']);
  }

  async refreshToken(): Promise<boolean> {
    const usuario = this._usuario();
    if (!usuario) return false;

    try {
      const novoUsuario: Usuario = await firstValueFrom(
        this.authHttp
          .refreshToken(usuario.refreshToken, usuario.login)
          .pipe(debounceTime(1000))
      );
      this.setUsuario(novoUsuario);
      return true;
    } catch {
      this.logout();
      return false;
    }
  }

  private carregarUsuarioDoStorage() {
    const usuario = this.authStorage.getUsuario();
    this._usuario.set(usuario);
  }

  private setUsuario(usuario: Usuario) {
    this._usuario.set(usuario);
    this.authStorage.saveUsuario(usuario);
  }

  get usuario(): Signal<Usuario | null> {
    return this._usuario.asReadonly();
  }

  isRefreshTokenExpired(): boolean {
    const usuario = this._usuario();
    if (!usuario) return true;

    const expiracao = DateTime.fromJSDate(
      new Date(usuario.expiracaoRefreshToken)
    ).setZone('America/Sao_Paulo');

    return expiracao.diffNow('minutes').minutes < 0;
  }

  isTokenExpired(): boolean {
    const usuario = this._usuario();
    if (!usuario) return true;

    const expiracao = DateTime.fromJSDate(
      new Date(usuario.expiracaoToken)
    ).setZone('America/Sao_Paulo');

    return expiracao.diffNow('minutes').minutes <= 0;
  }
}
